import type {
  ObservabilityFailureGroupItem,
  ObservabilityFailureQueueItem,
  ObservabilityFailureStatusCounts,
  ObservabilityFailureTransitionAction,
} from '@scatterin/api-types';
import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  ChevronRight,
  Clock3,
  Layers3,
  Loader2,
  RotateCcw,
  ScrollText,
  ShieldOff,
  SquareCheckBig,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';

import { JsonBlock } from '@/components/codeBlock';
import { ConfirmDialog } from '@/components/confirmDialog';
import { SearchInput } from '@/components/filterChips';
import { DetailList } from '@/components/keyValueGrid';
import { EmptyState, ErrorState } from '@/components/stateViews';
import { SeverityBadge, StatusBadge } from '@/components/statusBadges';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Drawer } from '@/components/ui/drawer';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableShell } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  useBulkFailureTransitionMutation,
  useFailureDetailQuery,
  useFailureGroupsQuery,
  useFailureTransitionMutation,
} from '@/hooks/useObservabilityQueries';
import { useRouteContext } from '@/hooks/useRouteContext';
import { formatInteger, formatRelativeTime, formatTime } from '@/lib/format';
import { drillThroughSearch } from '@/lib/navigation';
import { buildObservabilityFilter } from '@/lib/observabilityFilters';
import { matchesSearch } from '@/lib/search';
import type { AnalyticsUrlState } from '@/lib/urlState';

const failureStatusTabs = ['open', 'acknowledged', 'resolved', 'ignored', 'all'] as const;

const severityPriority: Record<string, number> = {
  critical: 0,
  error: 1,
  warning: 2,
  info: 3,
};

const validActions = (status: string): ObservabilityFailureTransitionAction[] => {
  if (status === 'open') return ['acknowledge', 'resolve', 'ignore'];
  if (status === 'acknowledged') return ['resolve', 'ignore', 'reopen'];
  if (status === 'resolved' || status === 'ignored') return ['reopen'];
  return [];
};

const actionLabel = (action: ObservabilityFailureTransitionAction): string => {
  if (action === 'acknowledge') return 'Acknowledge';
  if (action === 'resolve') return 'Resolve';
  if (action === 'ignore') return 'Ignore';
  return 'Reopen';
};

const actionPastTense = (action: ObservabilityFailureTransitionAction): string => {
  if (action === 'acknowledge') return 'acknowledged';
  if (action === 'resolve') return 'resolved';
  if (action === 'ignore') return 'ignored';
  return 'reopened';
};

const actionProgressLabel = (action: ObservabilityFailureTransitionAction): string => {
  if (action === 'acknowledge') return 'Acknowledging…';
  if (action === 'resolve') return 'Resolving…';
  if (action === 'ignore') return 'Ignoring…';
  return 'Reopening…';
};

const primaryActionIcon = (action: ObservabilityFailureTransitionAction, isPending: boolean) => {
  if (isPending) return <Loader2 aria-hidden="true" className="size-4 animate-spin" />;
  if (action === 'acknowledge') return <CheckCircle2 aria-hidden="true" className="size-4" />;
  return <RotateCcw aria-hidden="true" className="size-4" />;
};

const buttonVariant = (action: ObservabilityFailureTransitionAction) => {
  if (action === 'acknowledge') return 'primary';
  if (action === 'ignore') return 'danger';
  return 'secondary';
};

const bulkButtonVariant = (action: ObservabilityFailureTransitionAction) => {
  if (action === 'acknowledge') return 'primary';
  if (action === 'ignore') return 'ghost';
  return 'secondary';
};

const statusLabel = (status: string): string => {
  if (status === 'all') return 'All';
  return `${status.slice(0, 1).toUpperCase()}${status.slice(1)}`;
};

const humanize = (value: string | null | undefined): string =>
  value
    ? value
        .replaceAll(/[-_:]+/g, ' ')
        .replaceAll(/([a-z])([A-Z])/g, '$1 $2')
        .replaceAll(/\s+/g, ' ')
        .trim()
    : 'Unclassified failure';

const failureTitle = (item?: ObservabilityFailureQueueItem): string =>
  item?.failure.message?.trim() || humanize(item?.failure.errorType ?? item?.failure.category);

const failureService = (item?: ObservabilityFailureQueueItem): string =>
  item?.failure.serviceName?.trim() || item?.failure.source?.trim() || 'Unattributed service';

const failureCountLabel = (count: number): string => `${count} failure${count === 1 ? '' : 's'}`;

type PendingTransition = {
  action: ObservabilityFailureTransitionAction;
  affectedOccurrences: number;
  failureIds?: string[];
  groupKeys?: string[];
  mode: 'group' | 'single';
};

type FailureIssue = {
  group: ObservabilityFailureGroupItem;
  latest?: ObservabilityFailureQueueItem;
};

const issueMatchesClientFilters = (issue: FailureIssue, state: AnalyticsUrlState): boolean => {
  const matchesSeverity =
    state.failureSeverities.length === 0 ||
    state.failureSeverities.includes(issue.group.severity as AnalyticsUrlState['failureSeverities'][number]);
  const matchesGroup =
    state.failureGroupKeys.length === 0 ||
    state.failureGroupKeys.some((groupKey) => issue.group.groupKey.toLowerCase().includes(groupKey.toLowerCase()));
  const matchesRecurrence =
    state.failureRecurrence === 'all' ||
    (state.failureRecurrence === 'recurring' ? (issue.group.occurrenceCount ?? 0) > 1 : (issue.group.occurrenceCount ?? 0) <= 1);
  const matchesText = matchesSearch(state.search, [
    issue.group.groupKey,
    issue.group.category,
    issue.group.errorType,
    issue.group.severity,
    failureTitle(issue.latest),
    failureService(issue.latest),
    issue.latest?.failure.analysisType,
  ]);
  return matchesSeverity && matchesGroup && matchesRecurrence && matchesText;
};

const compareIssues = (left: FailureIssue, right: FailureIssue, sort: AnalyticsUrlState['failureSort']): number => {
  if (sort === 'latest') {
    return new Date(right.group.lastSeenAt).getTime() - new Date(left.group.lastSeenAt).getTime();
  }
  if (sort === 'occurrences') {
    return (
      (right.group.occurrenceCount ?? 0) - (left.group.occurrenceCount ?? 0) ||
      new Date(right.group.lastSeenAt).getTime() - new Date(left.group.lastSeenAt).getTime()
    );
  }
  return (
    (severityPriority[left.group.severity] ?? 99) - (severityPriority[right.group.severity] ?? 99) ||
    (right.group.unconsumedCount ?? 0) - (left.group.unconsumedCount ?? 0) ||
    new Date(right.group.lastSeenAt).getTime() - new Date(left.group.lastSeenAt).getTime()
  );
};

const buildIssues = (groups: ObservabilityFailureGroupItem[], state: AnalyticsUrlState): FailureIssue[] =>
  groups
    .map((group) => ({ group, latest: group.latestFailure ?? undefined }))
    .filter((issue) => issueMatchesClientFilters(issue, state))
    .toSorted((left, right) => compareIssues(left, right, state.failureSort));

const statusCount = (counts: ObservabilityFailureStatusCounts | undefined, status: (typeof failureStatusTabs)[number]): number => {
  if (!counts) return 0;
  if (status === 'all') {
    return counts.total ?? 0;
  }
  return counts[status] ?? 0;
};

const statusCountLabel = (counts: ObservabilityFailureStatusCounts | undefined, status: (typeof failureStatusTabs)[number]): string =>
  formatInteger(statusCount(counts, status));

const failurePageSearch = (state: AnalyticsUrlState, failureId?: string): string => {
  const search = new URLSearchParams({
    end: state.end.toISOString(),
    env: state.env,
    queueOffset: String(state.queueOffset),
    queueView: state.queueView,
    start: state.start.toISOString(),
  });
  const listValues: [string, string[]][] = [
    ['analysisTypes', state.analysisTypes],
    ['serviceNames', state.serviceNames],
    ['runtimeTypes', state.runtimeTypes],
    ['sources', state.sources],
    ['failureGroupKeys', state.failureGroupKeys],
    ['failureSeverities', state.failureSeverities],
  ];
  for (const [key, values] of listValues) {
    if (values.length > 0) search.set(key, values.join(','));
  }
  if (state.search) search.set('search', state.search);
  if (state.failureRecurrence !== 'all') search.set('failureRecurrence', state.failureRecurrence);
  if (state.failureSort !== 'priority') search.set('failureSort', state.failureSort);
  if (failureId) search.set('failureId', failureId);
  return search.toString();
};

const commonDrillScope = (state: AnalyticsUrlState) => ({
  analysisTypes: state.analysisTypes,
  end: state.end.toISOString(),
  env: state.env,
  runtimeTypes: state.runtimeTypes,
  serviceNames: state.serviceNames,
  sources: state.sources,
  start: state.start.toISOString(),
});

const FailureQueueLoading = () => (
  <Card aria-live="polite" role="status">
    <CardHeader>
      <CardTitle>Loading grouped failure queue</CardTitle>
    </CardHeader>
    <CardContent className="grid gap-3">
      {[0, 1, 2].map((row) => (
        <div className="border-line grid gap-3 rounded-lg border p-4" key={row}>
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-3 w-1/2" />
          <Skeleton className="h-3 w-1/3" />
        </div>
      ))}
    </CardContent>
  </Card>
);

const IssueIdentity = ({ issue }: { issue: FailureIssue }) => (
  <div className="min-w-0">
    <div className="flex flex-wrap items-center gap-2">
      <SeverityBadge value={issue.group.severity} />
      {(issue.group.occurrenceCount ?? 0) > 1 ? <Badge tone="amber">{formatInteger(issue.group.occurrenceCount)} recurring</Badge> : null}
    </div>
    <p className="text-ink mt-2 leading-5 font-semibold">{failureTitle(issue.latest)}</p>
    <p className="text-muted mt-1 text-sm">{failureService(issue.latest)}</p>
    {issue.latest?.failure.analysisType ? <p className="text-muted mt-1 text-xs">{humanize(issue.latest.failure.analysisType)}</p> : null}
  </div>
);

// This page coordinates query, filter, selection, detail, and mutation state in
// one operator workflow; the branch structure mirrors those explicit states.
// eslint-disable-next-line sonarjs/cognitive-complexity
export const FailuresPage = () => {
  const { setState, state } = useRouteContext();
  const groupsQuery = useFailureGroupsQuery(state);
  const detailQuery = useFailureDetailQuery(state);
  const transitionMutation = useFailureTransitionMutation();
  const bulkTransitionMutation = useBulkFailureTransitionMutation();
  const [pendingTransition, setPendingTransition] = useState<PendingTransition | null>(null);
  const [selectedGroupKeys, setSelectedGroupKeys] = useState<string[]>([]);

  const groups = groupsQuery.data?.groups ?? [];
  const issues = buildIssues(groups, state);
  const visibleGroupKeys = new Set(issues.map((issue) => issue.group.groupKey));
  const selectedVisibleGroupKeys = selectedGroupKeys.filter((groupKey) => visibleGroupKeys.has(groupKey));
  const selectedVisibleGroupKeySet = new Set(selectedVisibleGroupKeys);
  const selectedIssues = issues.filter((issue) => selectedVisibleGroupKeySet.has(issue.group.groupKey));
  const selectedOccurrenceCount = selectedIssues.reduce((count, issue) => count + (issue.group.occurrenceCount ?? 0), 0);
  const selectedItem = detailQuery.data?.items[0];
  const selectedIssue = state.failureId
    ? issues.find((issue) => issue.group.groupKey === (selectedItem?.groupKey ?? selectedItem?.failure.groupKey))
    : undefined;
  const hasClientFilters =
    Boolean(state.search) || state.failureGroupKeys.length > 0 || state.failureSeverities.length > 0 || state.failureRecurrence !== 'all';

  const toggleIssue = (issue: FailureIssue) => {
    setSelectedGroupKeys((current) =>
      current.includes(issue.group.groupKey)
        ? current.filter((groupKey) => groupKey !== issue.group.groupKey)
        : [...current, issue.group.groupKey],
    );
  };

  const transitionFailure = (failureId: string, action: ObservabilityFailureTransitionAction) => {
    transitionMutation.mutate(
      { action, environment: state.env, failureId },
      {
        onError: (error) => toast.error(error instanceof Error ? error.message : 'Failed to update failure'),
        onSuccess: () => {
          setPendingTransition(null);
          setState({ failureId: null });
          toast.success(`Failure ${actionPastTense(action)}`);
        },
      },
    );
  };

  const transitionGroups = (groupKeys: string[], action: ObservabilityFailureTransitionAction) => {
    if (groupKeys.length === 0 || state.queueView === 'all') return;
    bulkTransitionMutation.mutate(
      {
        action,
        environment: state.env,
        filter: buildObservabilityFilter(state),
        groupKeys,
        status: state.queueView,
      },
      {
        onError: (error) => toast.error(error instanceof Error ? error.message : 'Failed to update selected issue groups'),
        onSuccess: (response) => {
          setPendingTransition(null);
          setSelectedGroupKeys([]);
          toast.success(`Updated ${response.updatedCount ?? 0} failures`);
        },
      },
    );
  };

  const confirmTransition = () => {
    if (!pendingTransition) return;
    if (pendingTransition.mode === 'single' && pendingTransition.failureIds?.[0]) {
      transitionFailure(pendingTransition.failureIds[0], pendingTransition.action);
    } else if (pendingTransition.groupKeys) {
      transitionGroups(pendingTransition.groupKeys, pendingTransition.action);
    }
  };

  const bulkActions = selectedIssues.length === 0 || state.queueView === 'all' ? [] : validActions(state.queueView);

  const resetClientFilters = () =>
    setState({
      failureGroupKeys: [],
      failureRecurrence: 'all',
      failureSeverities: [],
      search: '',
    });

  const openSingleTransition = (action: ObservabilityFailureTransitionAction, failureId: string) =>
    setPendingTransition({ action, affectedOccurrences: 1, failureIds: [failureId], mode: 'single' });

  const openGroupTransition = (action: ObservabilityFailureTransitionAction, groupKeys: string[], affectedOccurrences: number) =>
    setPendingTransition({ action, affectedOccurrences, groupKeys, mode: 'group' });

  if (groupsQuery.isLoading) {
    return <FailureQueueLoading />;
  }

  if (groupsQuery.isError) {
    return <ErrorState message={groupsQuery.error.message} onRetry={() => void groupsQuery.refetch()} />;
  }

  const scopeSearch = commonDrillScope(state);
  const detailActions = selectedItem ? validActions(selectedItem.queueStatus) : [];
  const primaryDetailAction = detailActions.find((action) => action === 'acknowledge' || action === 'reopen');
  const secondaryDetailAction = detailActions.find((action) => action === 'resolve');
  const tertiaryDetailAction = detailActions.find((action) => action === 'ignore');
  const isTransitionPending = transitionMutation.isPending || bulkTransitionMutation.isPending;
  const primaryDetailPending =
    transitionMutation.isPending && pendingTransition?.mode === 'single' && pendingTransition.action === primaryDetailAction;
  const secondaryDetailPending =
    transitionMutation.isPending && pendingTransition?.mode === 'single' && pendingTransition.action === secondaryDetailAction;
  let drawerDescription = 'The selected occurrence was not found in this environment and time window.';
  let drawerTitle = 'Failure details unavailable';
  if (detailQuery.isLoading) {
    drawerDescription = 'Loading the selected occurrence.';
    drawerTitle = 'Loading failure details';
  }
  if (selectedItem) {
    drawerDescription = `${failureService(selectedItem)} · ${statusLabel(selectedItem.queueStatus)}`;
    drawerTitle = failureTitle(selectedItem);
  }
  let detailFallback = (
    <EmptyState title="Failure details are unavailable" tone="filtered">
      This occurrence was not found in the selected environment and time window.
    </EmptyState>
  );
  if (detailQuery.isLoading) {
    detailFallback = (
      <div className="grid gap-3" role="status">
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  } else if (detailQuery.isError) {
    detailFallback = <ErrorState message={detailQuery.error.message} onRetry={() => void detailQuery.refetch()} />;
  }

  return (
    <div className="grid min-w-0 gap-5">
      <ConfirmDialog
        confirmLabel={pendingTransition ? actionLabel(pendingTransition.action) : 'Confirm'}
        description={
          pendingTransition
            ? `${failureCountLabel(pendingTransition.affectedOccurrences)} will be ${actionPastTense(pendingTransition.action)} in ${state.env}. Group actions include every matching occurrence in the selected time window and status.`
            : undefined
        }
        isPending={transitionMutation.isPending || bulkTransitionMutation.isPending}
        onConfirm={confirmTransition}
        onOpenChange={(open) => {
          if (!open && !isTransitionPending) setPendingTransition(null);
        }}
        open={Boolean(pendingTransition)}
        pendingLabel={pendingTransition ? actionProgressLabel(pendingTransition.action) : 'Updating…'}
        title="Confirm failure queue update"
        variant={pendingTransition?.action === 'ignore' ? 'danger' : 'primary'}
      />

      <Drawer
        description={drawerDescription}
        onOpenChange={(open) => {
          if (!open) setState({ failureId: null });
        }}
        open={Boolean(state.failureId)}
        side="right"
        size="lg"
        title={drawerTitle}
      >
        {selectedItem ? (
          <div className="grid min-w-0 gap-5">
            <div className="flex flex-wrap items-center gap-2">
              <SeverityBadge value={selectedItem.failure.severity} />
              <StatusBadge value={selectedItem.queueStatus} />
              {selectedIssue?.group.occurrenceCount ? (
                <Badge tone={selectedIssue.group.occurrenceCount > 1 ? 'amber' : 'slate'}>
                  {formatInteger(selectedIssue.group.occurrenceCount)}{' '}
                  {selectedIssue.group.occurrenceCount === 1 ? 'occurrence' : 'occurrences'}
                </Badge>
              ) : null}
            </div>

            <div className="flex flex-wrap gap-2">
              {primaryDetailAction ? (
                <Button
                  aria-busy={primaryDetailPending}
                  disabled={isTransitionPending}
                  onClick={() => openSingleTransition(primaryDetailAction, selectedItem.failure.id)}
                  variant={buttonVariant(primaryDetailAction)}
                >
                  {primaryActionIcon(primaryDetailAction, primaryDetailPending)}
                  {primaryDetailPending ? actionProgressLabel(primaryDetailAction) : actionLabel(primaryDetailAction)}
                </Button>
              ) : null}
              {secondaryDetailAction ? (
                <Button
                  aria-busy={secondaryDetailPending}
                  disabled={isTransitionPending}
                  onClick={() => openSingleTransition(secondaryDetailAction, selectedItem.failure.id)}
                  variant="secondary"
                >
                  {secondaryDetailPending ? <Loader2 aria-hidden="true" className="size-4 animate-spin" /> : null}
                  {secondaryDetailPending ? actionProgressLabel(secondaryDetailAction) : actionLabel(secondaryDetailAction)}
                </Button>
              ) : null}
              {tertiaryDetailAction ? (
                <details className="relative">
                  <summary className="border-line text-muted hover:bg-selection flex min-h-10 cursor-pointer list-none items-center rounded-md border px-3.5 text-sm font-semibold">
                    More actions
                  </summary>
                  <div className="border-line bg-raised shadow-raised absolute right-0 z-10 mt-2 rounded-lg border p-1">
                    <Button
                      disabled={isTransitionPending}
                      onClick={() => openSingleTransition(tertiaryDetailAction, selectedItem.failure.id)}
                      variant="ghost"
                    >
                      <ShieldOff aria-hidden="true" className="text-critical size-4" />
                      Ignore
                    </Button>
                  </div>
                </details>
              ) : null}
            </div>

            <section>
              <h3 className="text-ink mb-3 text-sm font-semibold">Investigation context</h3>
              <DetailList
                items={[
                  { key: 'service', label: 'Service', value: failureService(selectedItem) },
                  {
                    key: 'analysis',
                    label: 'Workflow',
                    value: selectedItem.failure.analysisType ? humanize(selectedItem.failure.analysisType) : 'Not recorded',
                  },
                  { key: 'detected', label: 'Detected', value: formatTime(selectedItem.failure.detectedAt) },
                  { key: 'review', label: 'Review reason', value: selectedItem.reviewedReason || 'No review reason recorded' },
                ]}
              />
            </section>

            <div className="flex flex-wrap gap-2">
              {selectedItem.failure.runId ? (
                <Button asChild variant="secondary">
                  <Link
                    to={{
                      pathname: '/performance',
                      search: drillThroughSearch('', { ...scopeSearch, runId: selectedItem.failure.runId, view: 'runs' }),
                    }}
                  >
                    <Activity aria-hidden="true" className="size-4" />
                    Related run
                  </Link>
                </Button>
              ) : null}
              <Button asChild variant="secondary">
                <Link
                  to={{
                    pathname: '/explore',
                    search: drillThroughSearch('', {
                      ...scopeSearch,
                      failureId: selectedItem.failure.id,
                      runId: selectedItem.failure.runId,
                      view: 'logs',
                    }),
                  }}
                >
                  <ScrollText aria-hidden="true" className="size-4" />
                  Contextual logs
                </Link>
              </Button>
            </div>

            <details className="border-line rounded-lg border">
              <summary className="text-ink cursor-pointer px-4 py-3 text-sm font-semibold">Technical evidence</summary>
              <div className="border-line grid min-w-0 gap-4 border-t p-4">
                <DetailList
                  items={[
                    { key: 'failure-id', label: 'Failure ID', value: selectedItem.failure.id },
                    {
                      key: 'group-key',
                      label: 'Group key',
                      value: selectedItem.groupKey || selectedItem.failure.groupKey || 'Not grouped',
                    },
                    {
                      key: 'source-runtime',
                      label: 'Source / runtime',
                      value: `${selectedItem.failure.source} / ${selectedItem.failure.runtimeType}`,
                    },
                    { key: 'fingerprint', label: 'Fingerprint', value: selectedItem.failure.fingerprint || 'Not recorded' },
                    { key: 'run-id', label: 'Run ID', value: selectedItem.failure.runId || 'Not correlated' },
                    { key: 'project-id', label: 'Project ID', value: selectedItem.failure.projectId || 'Not recorded' },
                    { key: 'owner-id', label: 'Owner user ID', value: selectedItem.failure.ownerUserId || 'Not recorded' },
                    {
                      key: 'route-session',
                      label: 'Route / session',
                      value: `${selectedItem.failure.route || 'Not recorded'} / ${selectedItem.failure.sessionId || 'Not correlated'}`,
                    },
                    {
                      key: 'log-location',
                      label: 'Log location',
                      value: `${selectedItem.failure.logGroup || 'Not resolved'} / ${selectedItem.failure.logStream || 'Not resolved'}`,
                    },
                  ]}
                />
                {Object.keys(selectedItem.failure.attributes ?? {}).length > 0 ? (
                  <JsonBlock value={selectedItem.failure.attributes ?? {}} />
                ) : (
                  <p className="text-muted text-sm">No additional structured attributes were recorded.</p>
                )}
              </div>
            </details>
          </div>
        ) : (
          detailFallback
        )}
      </Drawer>

      <Tabs
        onValueChange={(value) => {
          setSelectedGroupKeys([]);
          setState({ failureId: null, queueOffset: 0, queueView: value as AnalyticsUrlState['queueView'] });
        }}
        value={state.queueView}
      >
        <div className="grid min-w-0 gap-4">
          <div className="overflow-x-auto pb-1">
            <TabsList>
              {failureStatusTabs.map((status) => (
                <TabsTrigger key={status} value={status}>
                  <span className="flex items-center gap-2">
                    {statusLabel(status)}
                    <span className="numeric text-xs">{statusCountLabel(groupsQuery.data?.statusCounts, status)}</span>
                  </span>
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
          <p className="text-muted text-xs">
            Each status count is the number of groups containing at least one occurrence in that state. A group can therefore appear in
            multiple status counts; All is the distinct group total.
          </p>

          <div className="grid gap-3 lg:grid-cols-[minmax(16rem,1fr)_auto] lg:items-end">
            <label className="text-muted grid gap-1.5 text-xs font-semibold">
              Search issues
              <SearchInput
                onChange={(search) => setState({ search })}
                placeholder="Search issue, service, workflow, or group"
                value={state.search}
              />
            </label>
            <Select
              label="Sort issues"
              onValueChange={(value) => setState({ failureSort: value as AnalyticsUrlState['failureSort'] })}
              options={[
                { label: 'Priority', value: 'priority' },
                { label: 'Latest occurrence', value: 'latest' },
                { label: 'Most occurrences', value: 'occurrences' },
              ]}
              value={state.failureSort}
            />
          </div>
        </div>

        <TabsContent value={state.queueView}>
          <Card>
            <CardHeader className="grid gap-3 lg:grid-cols-[1fr_auto] lg:items-center">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <CardTitle>{statusLabel(state.queueView)} issues</CardTitle>
                  {groupsQuery.isFetching ? <Badge tone="blue">Refreshing</Badge> : null}
                </div>
                <p className="text-muted mt-1 text-sm">
                  {formatInteger(issues.length)} shown · {formatInteger(groupsQuery.data?.total ?? groups.length)} grouped issues
                </p>
              </div>

              {selectedVisibleGroupKeys.length > 0 ? (
                <div className="border-line bg-sidebar flex flex-wrap items-center gap-2 rounded-lg border p-2">
                  <span className="text-ink px-2 text-sm font-semibold">
                    {formatInteger(selectedVisibleGroupKeys.length)} groups · {formatInteger(selectedOccurrenceCount)} occurrences
                  </span>
                  {bulkActions.map((action) => (
                    <Button
                      aria-busy={
                        bulkTransitionMutation.isPending && pendingTransition?.mode === 'group' && pendingTransition.action === action
                      }
                      disabled={isTransitionPending}
                      key={action}
                      onClick={() => openGroupTransition(action, selectedVisibleGroupKeys, selectedOccurrenceCount)}
                      variant={bulkButtonVariant(action)}
                    >
                      {bulkTransitionMutation.isPending && pendingTransition?.mode === 'group' && pendingTransition.action === action ? (
                        <Loader2 aria-hidden="true" className="size-4 animate-spin" />
                      ) : null}
                      {bulkTransitionMutation.isPending && pendingTransition?.mode === 'group' && pendingTransition.action === action
                        ? actionProgressLabel(action)
                        : actionLabel(action)}
                    </Button>
                  ))}
                  <Button onClick={() => setSelectedGroupKeys([])} variant="ghost">
                    Clear
                  </Button>
                </div>
              ) : (
                <span className="text-muted text-xs">
                  {state.queueView === 'all'
                    ? 'Choose a status tab to apply group actions.'
                    : 'Select an issue to act on every matching occurrence.'}
                </span>
              )}
            </CardHeader>

            <CardContent>
              {issues.length === 0 ? (
                <EmptyState
                  title={hasClientFilters ? 'No issues match these filters' : `No ${state.queueView} issues in this window`}
                  tone={hasClientFilters ? 'filtered' : 'neutral'}
                >
                  <div className="grid justify-items-center gap-3">
                    <p>
                      {hasClientFilters
                        ? 'The loaded queue contains no grouped issue matching the current search or advanced filters.'
                        : 'The endpoint returned zero grouped issues for this status and scope. This is not a health claim without collection evidence.'}
                    </p>
                    {hasClientFilters ? (
                      <Button onClick={resetClientFilters} variant="secondary">
                        Clear issue filters
                      </Button>
                    ) : null}
                  </div>
                </EmptyState>
              ) : (
                <>
                  <TableShell className="hidden md:block">
                    <Table
                      aria-label={`${statusLabel(state.queueView)} grouped failure queue`}
                      className="table-fixed"
                      style={{ minWidth: '58rem' }}
                    >
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-12">
                            <span className="sr-only">Select</span>
                          </TableHead>
                          <TableHead>Issue</TableHead>
                          <TableHead className="w-28">Occurrences</TableHead>
                          <TableHead className="w-28">Last seen</TableHead>
                          <TableHead className="w-40">State</TableHead>
                          <TableHead className="w-56 text-right">Next step</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {issues.map((issue) => {
                          const allSelected = selectedGroupKeys.includes(issue.group.groupKey);
                          const primaryAction = state.queueView === 'all' ? undefined : validActions(state.queueView)[0];
                          const isPrimaryActionPending =
                            bulkTransitionMutation.isPending &&
                            pendingTransition?.mode === 'group' &&
                            pendingTransition.action === primaryAction &&
                            pendingTransition.groupKeys?.includes(issue.group.groupKey);
                          return (
                            <TableRow key={issue.group.id}>
                              <TableCell>
                                <Checkbox
                                  aria-label={`Select ${failureTitle(issue.latest)}`}
                                  checked={allSelected}
                                  disabled={state.queueView === 'all'}
                                  onChange={() => toggleIssue(issue)}
                                />
                              </TableCell>
                              <TableCell className="max-w-xl">
                                <IssueIdentity issue={issue} />
                              </TableCell>
                              <TableCell>
                                <p className="numeric text-ink font-semibold">{formatInteger(issue.group.occurrenceCount)}</p>
                                <p className="text-muted mt-1 text-xs">{formatInteger(issue.group.unconsumedCount)} unreviewed</p>
                              </TableCell>
                              <TableCell>
                                <span title={formatTime(issue.group.lastSeenAt)}>{formatRelativeTime(issue.group.lastSeenAt)}</span>
                              </TableCell>
                              <TableCell>
                                <StatusBadge value={issue.latest?.queueStatus ?? state.queueView} />
                              </TableCell>
                              <TableCell>
                                <div className="flex justify-end gap-2">
                                  {primaryAction && issue.latest ? (
                                    <Button
                                      aria-busy={isPrimaryActionPending}
                                      aria-label={`${actionLabel(primaryAction)} ${failureTitle(issue.latest)}`}
                                      disabled={isTransitionPending}
                                      onClick={() =>
                                        openGroupTransition(primaryAction, [issue.group.groupKey], issue.group.occurrenceCount ?? 0)
                                      }
                                      variant={buttonVariant(primaryAction)}
                                    >
                                      {isPrimaryActionPending ? <Loader2 aria-hidden="true" className="size-4 animate-spin" /> : null}
                                      {isPrimaryActionPending ? actionProgressLabel(primaryAction) : actionLabel(primaryAction)}
                                    </Button>
                                  ) : null}
                                  {issue.latest ? (
                                    <Button asChild variant="ghost">
                                      <Link
                                        to={{
                                          pathname: '/failures',
                                          search: failurePageSearch(state, issue.latest.failure.id),
                                        }}
                                      >
                                        Details
                                        <ChevronRight aria-hidden="true" className="size-4" />
                                      </Link>
                                    </Button>
                                  ) : null}
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableShell>

                  <div className="grid gap-3 md:hidden">
                    {issues.map((issue) => {
                      const allSelected = selectedGroupKeys.includes(issue.group.groupKey);
                      const primaryAction = state.queueView === 'all' ? undefined : validActions(state.queueView)[0];
                      const isPrimaryActionPending =
                        bulkTransitionMutation.isPending &&
                        pendingTransition?.mode === 'group' &&
                        pendingTransition.action === primaryAction &&
                        pendingTransition.groupKeys?.includes(issue.group.groupKey);
                      return (
                        <article className="border-line rounded-lg border p-4" key={issue.group.id}>
                          <div className="flex items-start gap-3">
                            <Checkbox
                              aria-label={`Select ${failureTitle(issue.latest)}`}
                              checked={allSelected}
                              className="mt-1"
                              disabled={state.queueView === 'all'}
                              onChange={() => toggleIssue(issue)}
                            />
                            <div className="min-w-0 flex-1">
                              <IssueIdentity issue={issue} />
                            </div>
                          </div>
                          <dl className="border-line mt-4 grid grid-cols-2 gap-3 border-t pt-3 text-sm">
                            <div>
                              <dt className="text-muted text-xs font-semibold">Occurrences</dt>
                              <dd className="numeric text-ink mt-1">{formatInteger(issue.group.occurrenceCount)}</dd>
                            </div>
                            <div>
                              <dt className="text-muted text-xs font-semibold">Last seen</dt>
                              <dd className="text-ink mt-1">{formatRelativeTime(issue.group.lastSeenAt)}</dd>
                            </div>
                          </dl>
                          <div className="mt-4 flex flex-wrap gap-2">
                            {primaryAction && issue.latest ? (
                              <Button
                                aria-busy={isPrimaryActionPending}
                                className="flex-1"
                                disabled={isTransitionPending}
                                onClick={() => openGroupTransition(primaryAction, [issue.group.groupKey], issue.group.occurrenceCount ?? 0)}
                                variant={buttonVariant(primaryAction)}
                              >
                                {isPrimaryActionPending ? <Loader2 aria-hidden="true" className="size-4 animate-spin" /> : null}
                                {isPrimaryActionPending ? actionProgressLabel(primaryAction) : actionLabel(primaryAction)}
                              </Button>
                            ) : null}
                            {issue.latest ? (
                              <Button asChild className="flex-1" variant="secondary">
                                <Link
                                  to={{
                                    pathname: '/failures',
                                    search: failurePageSearch(state, issue.latest.failure.id),
                                  }}
                                >
                                  Details
                                  <ChevronRight aria-hidden="true" className="size-4" />
                                </Link>
                              </Button>
                            ) : null}
                          </div>
                        </article>
                      );
                    })}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
          {groupsQuery.data ? (
            <div className="mt-4 flex items-center justify-between gap-3">
              <Button
                disabled={state.queueOffset === 0}
                onClick={() => {
                  setSelectedGroupKeys([]);
                  setState({ failureId: null, queueOffset: Math.max(0, state.queueOffset - state.queueLimit) });
                }}
                variant="secondary"
              >
                <ArrowLeft aria-hidden="true" className="size-4" />
                Previous
              </Button>
              <span className="text-muted numeric text-xs">
                {groups.length === 0 ? 0 : state.queueOffset + 1}–{state.queueOffset + groups.length} of{' '}
                {formatInteger(groupsQuery.data.total)} groups
              </span>
              <Button
                disabled={!groupsQuery.data.hasMore}
                onClick={() => {
                  setSelectedGroupKeys([]);
                  setState({ failureId: null, queueOffset: state.queueOffset + state.queueLimit });
                }}
                variant="secondary"
              >
                Next
                <ArrowRight aria-hidden="true" className="size-4" />
              </Button>
            </div>
          ) : null}
        </TabsContent>
      </Tabs>

      <div className="text-muted flex flex-wrap items-center gap-2 text-xs">
        <Layers3 aria-hidden="true" className="size-4" />
        Counts represent distinct failure groups in the selected global scope. Search, severity, group, and recurrence narrow the loaded
        grouped queue.
        <Clock3 aria-hidden="true" className="ml-2 size-4" />
        Latest occurrences remain ordered within each group.
        <SquareCheckBig aria-hidden="true" className="ml-2 size-4" />
        Group actions apply to every matching occurrence in the selected time window and status.
        <AlertTriangle aria-hidden="true" className="ml-2 size-4" />
        Ignore remains a tertiary action.
      </div>
    </div>
  );
};
